import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { globeIcon, warningIcon, targetIcon } from 'src/app/app-icons';
import { OrganisationUnitModel } from 'src/app/models/organisation-unit/organisation-unit.model';
import { TrainingGroupModel } from 'src/app/models/training-group.model';
import { DxTabsTypes } from 'devextreme-angular/ui/tabs';
import { DxTreeViewComponent } from 'devextreme-angular';
import { EventVisibilityModeModel } from 'src/app/models/event-visibility-mode.model';
import { TenantSubscriberModel } from 'src/app/models/subscription/tenant-subscriber.model';
import { StandardTrainingCategoryViewModel } from 'src/app/models/standard-training-category/standard-training-category-view.model';
import { RoleModel } from 'src/app/models/person/role.model';

@Component({
  selector: 'app-employee-visibility',
  standalone: false,
  templateUrl: './employee-visibility.component.html',
  styleUrl: './employee-visibility.component.scss',
})
export class EmployeeVisibilityComponent {
  @ViewChild('trainingGroupsTree') 
  trainingGroupsTree!: DxTreeViewComponent;

  @ViewChild('rolesTree') 
  rolesTree!: DxTreeViewComponent;

  @Input() isCourse = false;
  @Input() isPrivatePublishing = false;

  @Input() visibilityMode!: EventVisibilityModeModel;
  @Output() visibilityModeChange = new EventEmitter<EventVisibilityModeModel>();

  @Input() organizationUnitIds: string[] = [];
  @Output() organizationUnitIdsChange = new EventEmitter<string[]>();

  @Input() trainingGroupIds: string[] = [];
  @Output() trainingGroupIdsChange = new EventEmitter<string[]>();

  @Input() allowSharingWithSubscribers: boolean = false;
  @Output() allowSharingWithSubscribersChange = new EventEmitter<boolean>();

  @Input() subscriptionIds: string[] = [];
  @Output() subscriptionIdsChange = new EventEmitter<string[]>();

  @Input() standardTrainingCategoryIds: string[] = [];
  @Output() standardTrainingCategoryIdsChange = new EventEmitter<string[]>();

  @Input() roleIds: string[] = [];
  @Output() roleIdsChange = new EventEmitter<string[]>();

  @Input() organizationUnits: OrganisationUnitModel[] = [];
  @Input() trainingGroups: TrainingGroupModel[] = [];
  @Input() subscribers: TenantSubscriberModel[] = [];
  @Input() standardTrainingCategories: StandardTrainingCategoryViewModel[] = [];
  @Input() roles: RoleModel[] = [];
  @Input() usedStandardTrainingCategories: StandardTrainingCategoryViewModel[] = [];

  @Input() employeeCount = 0;
  @Input() isViewMode = false;

  EventVisibilityModeModel = EventVisibilityModeModel;

  visibilityModes: any[] = [
    {
      id: EventVisibilityModeModel.Everyone,
      text: this.translateService.instant(
        'TARGET-GROUP.TARGET-GROUP-TABS.EVERYONE-IN-ORGANIZATION'
      ),
    },
    {
      id: EventVisibilityModeModel.Restricted,
      text: this.translateService.instant(
        'TARGET-GROUP.TARGET-GROUP-TABS.SPECIFIC-PEOPLE'
      ),
    },
  ];

  get isVisibleToEveryone() {
    return this.isAllOrganizationUnitsSelected &&
      this.trainingGroupIds.length == 0 &&
      this.roleIds.length == 0;
  }

  get isAllOrganizationUnitsSelected() {
    const selectedOrgUnitCount = 
      this.organizationUnitIds.length;
      
    return selectedOrgUnitCount == 0
      || selectedOrgUnitCount == this.organizationUnits.length;
  }

  constructor(
    private translateService: TranslateService,
    private sanitized: DomSanitizer,
  ) {
  }

  getClockIcon() {
    return this.sanitized.bypassSecurityTrustHtml(globeIcon);
  }

  getWarningIcon() {
    return this.sanitized.bypassSecurityTrustHtml(warningIcon);
  }

  getTargetIcon() {
    return this.sanitized.bypassSecurityTrustHtml(targetIcon);
  }
  
  getVisibleToEveryoneText() {
    return this.translateService.instant(
      'TARGET-GROUP.VISIBLE-TO-EVERYONE',
      {
        employeeCount: this.employeeCount
      }
    );
  }

  getEmployeeVisibleInText() {
    let text = this.translateService.instant(
      'TARGET-GROUP.VISIBLE-TO-EMPLOYEES'
    );

    const parts: string[] = [];

    if (this.organizationUnitIds.length
      && !this.isAllOrganizationUnitsSelected
    ) {
      const orgUnits = this.organizationUnits
        .filter(ou => this.organizationUnitIds.includes(ou.id))
        .map(ou => ou.name);

      parts.push(
        `${this.translateService.instant('COMMON.IN')} <b>${orgUnits.join(', ')}</b>`
      );
    }

    if (this.trainingGroupIds?.length) {
      const groups = this.trainingGroups
        .filter(tg => this.trainingGroupIds.includes(tg.id))
        .map(tg => tg.name);

      parts.push(
        `${this.translateService.instant('COMMON.IN-GROUPS')} <b>${groups.join(', ')}</b>`
      );
    }

    if (this.roleIds?.length) {
      const roles = this.roles
        .filter(r => this.roleIds.includes(r.id))
        .map(r => r.name);

      parts.push(
        `${this.translateService.instant('COMMON.IN-ROLES')} <b>${roles.join(', ')}</b>`
      );
    }

    return `${text} ${parts.join(' · ')}`;
  }

    
  onVisibilityModeChange(e: DxTabsTypes.ItemClickEvent) {
    this.visibilityMode = e.itemData.id;
    this.visibilityModeChange.emit(this.visibilityMode);
  }

  resetTargetData() {
    this.organizationUnitIds = [];
    this.organizationUnitIdsChange.emit(
      this.organizationUnitIds
    );

    this.trainingGroupIds = [];
    this.trainingGroupIdsChange.emit(
      this.trainingGroupIds
    )
  }
  
  onOrganizationUnitsSelected(e: any) {
    const currentSelectionId = e.itemData.id;
    const isSelection = e.node.selected;

    const currentUnit = this.organizationUnits.find(
      (unit) => unit.id === currentSelectionId
    );

    if (currentUnit) {
      currentUnit.selected = isSelection;
    }

    const setChildrenSelection = (parentId: string, selected: boolean) => {
      this.organizationUnits.forEach((unit) => {
        if (unit.parentId === parentId) {
          unit.selected = selected;
          setChildrenSelection(unit.id, selected);
        }
      });
    };

    setChildrenSelection(currentSelectionId, isSelection);

    const checkAndDeselectParents = (childParentId: string | null) => {
      if (!childParentId) return;

      const siblings = this.organizationUnits.filter(
        (unit) => unit.parentId === childParentId
      );
      const allSelected = siblings.every((s) => s.selected);

      if (!allSelected) {
        const parentUnit = this.organizationUnits.find(
          (unit) => unit.id === childParentId
        );
        if (parentUnit && parentUnit.selected) {
          parentUnit.selected = false;
          checkAndDeselectParents(parentUnit.parentId);
        }
      }
    };

    if (!isSelection) {
      checkAndDeselectParents(currentUnit?.parentId ?? null);
    }

    this.organizationUnitIds = this.organizationUnits
      .filter((unit) => unit.selected)
      .map((unit) => unit.id);

    this.organizationUnitIdsChange.emit(
      this.organizationUnitIds
    );

    this.organizationUnits = [...this.organizationUnits];
  }

  onTrainingGroupSelected(e: any) {
    if (e.node.selected) {
      this.trainingGroupIds = [
        ...this.trainingGroupIds,
        e.node.key,
      ];
    } else {
      this.trainingGroupIds =
        this.trainingGroupIds.filter((id) => id !== e.node.key);
    }

    this.trainingGroupIdsChange.emit(
      this.trainingGroupIds
    )
  }

  onTrainingGroupsValueChanged(e: any): void {
    if (e.value === null) {
      this.trainingGroupIds = [];
      this.trainingGroupIdsChange.emit(this.trainingGroupIds);

      this.trainingGroupsTree?.instance.unselectAll();
      this.setTreeViewValues();
    }
  }

  onAllowSharingWithSubscribersChanged(
    allowSharingWithSubscribers: boolean
  ) {
    this.allowSharingWithSubscribers = 
      allowSharingWithSubscribers;

    this.allowSharingWithSubscribersChange.emit(
      allowSharingWithSubscribers
    );

    if(
      allowSharingWithSubscribers && 
      this.subscriptionIds.length == 0
    ) {
      this.selectAllSubscriptions();
    }
  }

  onRoleSelected(e: any) {
    if (e.node.selected) {
      this.roleIds = [
        ...this.roleIds,
        e.node.key,
      ];
    } else {
      this.roleIds = this.roleIds.filter(
        (id) => id !== e.node.key
      );
    }

    this.roleIdsChange.emit(this.roleIds);
  }

  onRolesValueChanged(e: any): void {
    if (e.value === null) {
      this.roleIds = [];
      this.roleIdsChange.emit(this.roleIds);

      this.rolesTree?.instance.unselectAll();
      this.setTreeViewValues();
    }
  }


  private selectAllSubscriptions() {
    this.subscriptionIds = this.subscribers
      .map(s => s.subscriptionId)

    this.subscriptionIdsChange.emit(
      this.subscriptionIds
    )

    this.setTreeViewValues();
  }

  setTreeViewValues(): void {
    this.trainingGroups.forEach((trainingGroup) => {
      trainingGroup.selected = this.trainingGroupIds.includes(
        trainingGroup.id
      );
    });

    this.organizationUnits.forEach((unit) => {
      unit.selected = this.organizationUnitIds.includes(unit.id);
    });

    this.subscribers.forEach((subscriber) => {
      subscriber.selected = this.subscriptionIds.includes(
        subscriber.subscriptionId
      );
    });

    this.roles.forEach((role) => {
      role.selected = this.roleIds.includes(role.id);
    });
  }
} 
